import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  absoluteUrl,
  getHreflangTriple,
  getOgImageUrl,
  getSiteUrl,
  parseLocalizedPathname
} from '../lib/siteUrl'

const siteBase = () => `${getSiteUrl()}/`
const SITE_NAME = 'VIVA Psychotherapy'

const DATA_ATTR = 'data-page-seo'

/**
 * Per-route SEO head tags (SPA). Cleans up injected nodes on unmount.
 *
 * @param {object} options
 * @param {boolean} options.enabled - set false while translations loading
 * @param {string} options.title - full document title (include site name if desired)
 * @param {string} options.description
 * @param {string} [options.keywords]
 * @param {string} [options.pathname] - override location.pathname for canonical/hreflang
 * @param {boolean} [options.includeHreflang=true]
 * @param {string} [options.ogType='website']
 * @param {string} [options.imageUrl] - absolute OG image (default: site og-share.png)
 * @param {string} [options.imageAlt]
 * @param {string} [options.locale] - 'en_US' | 'pt_BR' for og:locale
 */
export function usePageSeo ({
  enabled = true,
  title,
  description,
  keywords,
  pathname: pathnameOverride,
  includeHreflang = true,
  ogType = 'website',
  imageUrl,
  imageAlt,
  locale
}) {
  const location = useLocation()
  const pathname = pathnameOverride ?? location.pathname

  useEffect(() => {
    if (!enabled || !title || description == null) return undefined

    document.title = title

    const { suffix, lang: pathLang } = parseLocalizedPathname(pathname)
    const htmlLang = pathLang === 'pt' ? 'pt' : 'en'
    document.documentElement.lang = htmlLang

    const upsertMetaByName = (name, content, { scoped = true } = {}) => {
      const sel = scoped
        ? `meta[name="${name}"][${DATA_ATTR}]`
        : `meta[name="${name}"]`
      let el = scoped ? document.querySelector(sel) : document.querySelector(`meta[name="${name}"]`)
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute('name', name)
        if (scoped) el.setAttribute(DATA_ATTR, '')
        document.head.appendChild(el)
      } else if (scoped && !el.hasAttribute(DATA_ATTR)) {
        el.setAttribute(DATA_ATTR, '')
      }
      el.setAttribute('content', content)
    }

    const upsertMetaProperty = (property, content) => {
      const sel = `meta[property="${property}"][${DATA_ATTR}]`
      let el = document.querySelector(sel)
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute('property', property)
        el.setAttribute(DATA_ATTR, '')
        document.head.appendChild(el)
      }
      el.setAttribute('content', content)
    }

    upsertMetaByName('description', description, { scoped: false })
    if (keywords) {
      upsertMetaByName('keywords', keywords, { scoped: false })
    }

    const contentLanguage = htmlLang === 'pt' ? 'pt-BR' : 'en-US'
    upsertMetaByName('content-language', contentLanguage, { scoped: true })

    const geoMeta = [
      { name: 'geo.region', content: 'US-FL' },
      { name: 'geo.placename', content: 'Boca Raton' },
      { name: 'geo.position', content: '26.3683;-80.1289' },
      { name: 'ICBM', content: '26.3683, -80.0887' }
    ]
    geoMeta.forEach(({ name, content }) => {
      upsertMetaByName(name, content, { scoped: true })
    })

    const canonicalUrl = absoluteUrl(pathname === '' ? '/' : pathname)
    let canonical =
      document.querySelector(`link[rel="canonical"][${DATA_ATTR}]`) ||
      document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute(DATA_ATTR, '')
    canonical.setAttribute('href', canonicalUrl)

    const og = imageUrl || getOgImageUrl()
    const customImage = Boolean(imageUrl)
    const ogTypeMime = customImage && /\.jpe?g(\?|$)/i.test(og) ? 'image/jpeg' : 'image/png'
    const ogAlt = imageAlt || (customImage ? title : 'VIVA Psychotherapy logo')
    upsertMetaProperty('og:type', ogType)
    upsertMetaProperty('og:url', canonicalUrl)
    upsertMetaProperty('og:title', title)
    upsertMetaProperty('og:description', description)
    upsertMetaProperty('og:image', og)
    upsertMetaProperty('og:image:secure_url', og)
    upsertMetaProperty('og:image:type', ogTypeMime)
    if (!customImage) {
      upsertMetaProperty('og:image:width', '2400')
      upsertMetaProperty('og:image:height', '1260')
    }
    upsertMetaProperty('og:image:alt', ogAlt)
    upsertMetaProperty('og:site_name', SITE_NAME)

    const ogLocale = locale || (pathLang === 'pt' ? 'pt_BR' : 'en_US')
    upsertMetaProperty('og:locale', ogLocale)
    upsertMetaProperty('og:locale:alternate', ogLocale === 'pt_BR' ? 'en_US' : 'pt_BR')

    upsertMetaProperty('twitter:card', 'summary_large_image')
    upsertMetaProperty('twitter:url', canonicalUrl)
    upsertMetaProperty('twitter:title', title)
    upsertMetaProperty('twitter:description', description)
    upsertMetaProperty('twitter:image', og)
    upsertMetaProperty('twitter:image:alt', ogAlt)

    if (includeHreflang) {
      const triple = getHreflangTriple(suffix)
      const pairs = [
        { code: 'en', href: triple.en },
        { code: 'pt', href: triple.pt },
        { code: 'x-default', href: triple.xDefault }
      ]
      pairs.forEach(({ code, href }) => {
        const sel = `link[rel="alternate"][${DATA_ATTR}][data-hreflang="${code}"]`
        let link = document.querySelector(sel)
        if (!link) {
          link = document.createElement('link')
          link.setAttribute('rel', 'alternate')
          link.setAttribute(DATA_ATTR, '')
          link.setAttribute('data-hreflang', code)
          document.head.appendChild(link)
        }
        link.setAttribute('hreflang', code)
        link.setAttribute('href', href)
      })
    }

    return () => {
      document.querySelectorAll(`[${DATA_ATTR}]`).forEach((el) => {
        if (el.tagName === 'LINK' && el.getAttribute('rel') === 'canonical') {
          el.setAttribute('href', siteBase())
        } else {
          el.remove()
        }
      })
    }
  }, [
    enabled,
    title,
    description,
    keywords,
    pathname,
    includeHreflang,
    ogType,
    imageUrl,
    imageAlt,
    locale
  ])
}

/** Re-export helpers for pages that build JSON-LD manually */
export { getSiteUrl, absoluteUrl, getOgImageUrl, getHreflangTriple, parseLocalizedPathname }
